import { createFileRoute, useNavigate } from '@tanstack/react-router'
import type { AdminFeedbackListItem } from '@workspace/api-backend'
import type { TableColumn } from '@workspace/ui/components/data-table'
import { CellText, DataTable } from '@workspace/ui/components/data-table'
import { Pagination } from '@workspace/ui/components/pagination'
import { SearchInput } from '@workspace/ui/components/search-input'
import { useState } from 'react'
import { useAdminFeedbackQuery } from '@/hooks/api'
import { CellLink } from '@/utils/data-table'
import { formatFullDate } from '@/utils/format'

type SearchParams = {
  page?: number
  search?: string
}

export const Route = createFileRoute('/_protected/admin/feedback/')({
  component: AdminFeedbackPage,
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    page: Number(search.page) || 1,
    search: (search.search as string) || undefined,
  }),
})

const LIMIT = 20

const columns: TableColumn<AdminFeedbackListItem>[] = [
  {
    header: 'ID',
    width: '100px',
    render: (item) => <CellText className="font-mono text-xs">{item.id.slice(0, 8)}</CellText>,
  },
  {
    header: 'Message',
    render: (item) => (
      <p className="whitespace-pre-line text-foreground">
        {item.message.replace(/\n{3,}/g, '\n\n').trim()}
      </p>
    ),
  },
  {
    header: 'User',
    width: '180px',
    render: (item) => (
      <CellLink to="/admin/users/$id" params={{ id: item.user.id }} className="text-xs">
        {item.user.name || item.user.email}
      </CellLink>
    ),
  },
  {
    header: 'Page',
    width: '200px',
    render: (item) => (
      <span className="truncate text-xs text-muted-foreground" title={item.pageUrl}>
        {item.pageUrl.replace(/^https?:\/\/[^/]+/, '')}
      </span>
    ),
  },
  {
    header: 'Created',
    width: '130px',
    render: (item) => <CellText>{formatFullDate(item.createdAt)}</CellText>,
  },
]

function AdminFeedbackPage() {
  const { page = 1, search } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const [searchInput, setSearchInput] = useState(search ?? '')

  const feedbackQuery = useAdminFeedbackQuery({
    query: { page, limit: LIMIT, search: search || undefined },
  })

  const feedbackItems = feedbackQuery.data?.data?.items ?? []
  const total = feedbackQuery.data?.data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / LIMIT))

  return (
    <div className="w-full space-y-6 py-6">
      <SearchInput
        placeholder="Search feedback..."
        value={searchInput}
        onChange={setSearchInput}
        onSubmit={() => navigate({ search: { page: 1, search: searchInput || undefined } })}
      />
      <DataTable
        columns={columns}
        data={feedbackItems}
        isLoading={feedbackQuery.isLoading}
        emptyMessage="No feedback found."
        keyExtractor={(f) => f.id}
      />
      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={(p) => navigate({ search: { page: p, search } })}
      />
    </div>
  )
}
